import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { allCategories, allSpecials } from "@/lib/content";
import SearchField from "@/components/feature/SearchField";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/category/all", label: "分类" },
  { to: "/special/midnight-tokyo", label: "专题" },
  { to: "/about", label: "关于" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<"category" | "special" | null>(null);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setOpenMenu(null);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen || searchOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen, searchOpen]);

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to === "/category/all") return location.pathname.startsWith("/category");
    if (to === "/special/midnight-tokyo") return location.pathname.startsWith("/special");
    return location.pathname.startsWith("/about");
  };

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-background-300/50 bg-background-50/90 backdrop-blur-md">
        <div className="flex h-16 w-full items-center justify-between gap-3 px-4 md:h-[72px] md:px-8">
          <Link to="/" className="flex min-w-0 cursor-pointer items-center gap-2.5">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md bg-primary-500 font-heading text-[15px] font-extrabold text-background-950">
              亚
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate font-heading text-[15px] font-bold tracking-wide text-foreground-50 md:text-base">
                亚洲无码
              </span>
              <span className="hidden text-[9px] uppercase tracking-[0.22em] text-foreground-500 sm:block">
                Asian Uncensored Archive
              </span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex">
            {navItems.map((item) => (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => {
                  if (item.label === "分类") setOpenMenu("category");
                  if (item.label === "专题") setOpenMenu("special");
                }}
                onMouseLeave={() => setOpenMenu(null)}
              >
                <Link
                  to={item.to}
                  className={`flex h-9 cursor-pointer items-center gap-1 rounded-md px-3 text-sm transition ${
                    isActive(item.to)
                      ? "bg-background-200 text-primary-300"
                      : "text-foreground-300 hover:bg-background-100 hover:text-foreground-50"
                  }`}
                >
                  {item.label}
                  {item.label === "分类" || item.label === "专题" ? (
                    <i className="ri-arrow-down-s-line text-xs text-foreground-500" />
                  ) : null}
                </Link>

                {item.label === "分类" && openMenu === "category" ? (
                  <div className="absolute left-0 top-full w-[420px] pt-2">
                    <div className="fade-in overflow-hidden rounded-lg border border-background-300/70 bg-background-100 p-2">
                      <div className="grid grid-cols-2 gap-1">
                        {allCategories.map((category) => (
                          <Link
                            key={category.key}
                            to={`/category/${category.key}`}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 transition hover:bg-background-200"
                          >
                            <span className="flex h-7 w-7 items-center justify-center rounded bg-background-300/70 text-primary-400">
                              <i className={`${category.icon} text-sm`} />
                            </span>
                            <span className="min-w-0">
                              <span className="block text-[13px] text-foreground-100">{category.name}</span>
                              <span className="block text-[10px] uppercase tracking-wide text-foreground-600">
                                {category.en}
                              </span>
                            </span>
                          </Link>
                        ))}
                      </div>
                      <Link
                        to="/category/all"
                        className="mt-1 flex cursor-pointer items-center justify-between rounded-md bg-primary-500 px-3 py-2 text-[12px] font-semibold text-background-950 transition hover:bg-primary-400"
                      >
                        浏览全部 50 部作品
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </div>
                ) : null}

                {item.label === "专题" && openMenu === "special" ? (
                  <div className="absolute left-0 top-full w-[320px] pt-2">
                    <div className="fade-in overflow-hidden rounded-lg border border-background-300/70 bg-background-100 p-2">
                      {allSpecials.map((special) => (
                        <Link
                          key={special.id}
                          to={`/special/${special.id}`}
                          className="flex cursor-pointer items-center justify-between gap-2 rounded-md px-2.5 py-2.5 transition hover:bg-background-200"
                        >
                          <span className="min-w-0">
                            <span className="block truncate text-[13px] text-foreground-100">{special.title}</span>
                            <span className="block text-[10px] uppercase tracking-wide text-foreground-600">
                              {special.subtitle}
                            </span>
                          </span>
                          <span className="flex-shrink-0 rounded bg-background-300/70 px-1.5 py-0.5 text-[10px] text-foreground-400">
                            {special.workIds.length} 部
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </nav>

          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="打开搜索"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-300 transition hover:border-primary-600/70 hover:text-primary-300"
            >
              <i className="ri-search-line text-base" />
            </button>
            <span className="hidden h-9 items-center rounded-md border border-accent-500/50 px-2.5 font-heading text-[11px] font-bold text-accent-300 sm:flex">
              18+
            </span>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              aria-label="打开菜单"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-200 transition hover:border-primary-600/70 hover:text-primary-300 md:hidden"
            >
              <i className="ri-menu-line text-lg" />
            </button>
          </div>
        </div>
      </header>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 md:hidden">
          <button
            type="button"
            aria-label="关闭菜单"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 cursor-pointer bg-background-950/70"
          />
          <div className="fade-in absolute right-0 top-0 h-full w-[86%] max-w-[360px] overflow-y-auto border-l border-background-300/60 bg-background-100 px-4 pb-8">
            <div className="flex h-16 items-center justify-between">
              <span className="font-heading text-sm font-semibold tracking-wide text-foreground-100">导航</span>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                aria-label="关闭菜单"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-300"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="mt-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-3 text-[15px] transition ${
                    isActive(item.to)
                      ? "bg-background-200 text-primary-300"
                      : "text-foreground-200 hover:bg-background-200"
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-600" />
                </Link>
              ))}
            </div>

            <div className="mt-5">
              <p className="mb-2 px-1 font-heading text-[11px] uppercase tracking-[0.2em] text-foreground-600">
                Categories
              </p>
              <div className="space-y-1">
                {allCategories.map((category) => (
                  <Link
                    key={category.key}
                    to={`/category/${category.key}`}
                    className="flex cursor-pointer items-center justify-between rounded-md border border-background-300/50 px-3 py-2.5 text-[13px] text-foreground-200 transition hover:border-primary-700/60 hover:text-primary-300"
                  >
                    <span className="flex items-center gap-2">
                      <i className={`${category.icon} text-primary-500`} />
                      {category.name}
                    </span>
                    <i className="ri-arrow-right-s-line text-foreground-600" />
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-2 px-1 font-heading text-[11px] uppercase tracking-[0.2em] text-foreground-600">
                Specials
              </p>
              {allSpecials.map((special) => (
                <Link
                  key={special.id}
                  to={`/special/${special.id}`}
                  className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2.5 text-[13px] text-foreground-200 transition hover:bg-background-200"
                >
                  <span className="truncate pr-2">{special.title}</span>
                  <span className="flex-shrink-0 text-[10px] text-foreground-600">{special.workIds.length} 部</span>
                </Link>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-accent-600/40 bg-accent-950/40 p-3 text-[11px] leading-relaxed text-foreground-400">
              本站仅面向 18 岁以上用户，内容为作品资料与目录信息展示，不提供播放与下载。
            </div>
          </div>
        </div>
      ) : null}

      {searchOpen ? (
        <div className="fade-in fixed inset-0 z-50 bg-background-50/98 backdrop-blur-sm">
          <div className="mx-auto flex h-full w-full max-w-3xl flex-col px-4 pt-5">
            <div className="flex items-center justify-between">
              <span className="font-heading text-sm uppercase tracking-[0.2em] text-foreground-500">全站搜索</span>
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="关闭搜索"
                className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-200"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="mt-6">
              <SearchField autoFocus size="lg" onDone={() => setSearchOpen(false)} />
            </div>
            <div className="mt-8">
              <p className="mb-3 text-[11px] uppercase tracking-[0.2em] text-foreground-600">热门检索</p>
              <div className="flex flex-wrap gap-2">
                {["夜色东京", "花魁物语", "都市夜归人", "古装", "悬疑", "密室"].map((word) => (
                  <Link
                    key={word}
                    to={`/category/all?keyword=${encodeURIComponent(word)}`}
                    onClick={() => setSearchOpen(false)}
                    className="cursor-pointer rounded-full border border-background-300/70 px-3.5 py-1.5 text-xs text-foreground-300 transition hover:border-primary-600/70 hover:text-primary-300"
                  >
                    {word}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}